import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabaseClient";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { userCreatedToast } from '@/lib/welcomeEmailNotice';
import { TEAM_ASSIGNABLE_ROLES } from '@/lib/teamAssignableRoles';

// Define interfaces for type safety
interface Role {
  id: string;
  name: string;
}

// Available roles for team-based users. F-142: its own module so a test can pin the ids.
const TEAM_AVAILABLE_ROLES = TEAM_ASSIGNABLE_ROLES;

interface NewUser {
  first_name: string;
  last_name: string;
  email: string;
  role_id: string;
  phone_number: string;
  team_ids: string[];
  manager_id: string;
}

interface AddUserPageProps {
  onClose?: () => void;
}

export default function AddUserPage({ onClose }: AddUserPageProps = {}) {
  const [newUser, setNewUser] = useState<NewUser>({
    first_name: "",
    last_name: "",
    email: "",
    role_id: "",
    phone_number: "",
    team_ids: [],
    manager_id: "",
  });
  const [roleName, setRoleName] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [managerName, setManagerName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [isEmailChecking, setIsEmailChecking] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { hasPermission, roleInfo } = usePermissions();

  // Check if current user is Team Owner or Team Admin (simplified form)
  const isTeamBasedUser = roleInfo?.name === 'Team Owner' || roleInfo?.name === 'Team Admin';

  useEffect(() => {
    fetchRoles();
    if (user) {
      fetchUserData();
    }
  }, [user, isTeamBasedUser]);

  const fetchRoles = async () => {
    if (isTeamBasedUser) {
      // Team Owners/Admins can choose from multiple roles - default to Sales Rep
      setRoleName('Sales Rep');
      setNewUser(prev => ({ ...prev, role_id: '1' }));
    } else {
      // For admin users, load the default role (still Sales Rep for now)
      const { data, error } = await supabase.from('roles').select('id, name').eq('id', '1').single();
      if (error) {
        toast({ title: "Error", description: "Failed to load role.", variant: "destructive" });
      } else {
        setRoleName(data.name);
        setNewUser(prev => ({ ...prev, role_id: data.id }));
      }
    }
  };

  const fetchUserData = async () => {
    if (!user) {
      toast({ title: "Error", description: "User not authenticated.", variant: "destructive" });
      return;
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();
      if (userError) throw new Error('Failed to fetch user details: ' + userError.message);

      setManagerName(`${userData.first_name} ${userData.last_name}`);

      // Fetch sales teams separately (integer IDs only)
      const { data: salesTeamData, error: salesTeamError } = await supabase
        .from('user_teams')
        .select(`
          team_id,
          teams!inner(name)
        `)
        .eq('user_id', user.id);
      
      if (salesTeamError) {
        console.error('Error fetching sales teams:', salesTeamError);
      }

      // Fetch installer partners separately
      let installerPartnerData = null;
      try {
        const { data, error: installerError } = await supabase
          .from('user_installer_partners')
          .select(`
            installer_partner_id,
            installer_partners!inner(company_name)
          `)
          .eq('user_id', user.id);
        
        installerPartnerData = data;
        if (installerError) {
          console.error('Error fetching installer partners:', installerError);
        }
      } catch (tableError) {
        console.warn('user_installer_partners table not accessible:', tableError);
        installerPartnerData = [];
      }

      // Combine team names for display
      const salesTeamNames = salesTeamData?.map((st: any) => st.teams?.name).filter(Boolean) || [];
      const installerPartnerNames = installerPartnerData?.map((ip: any) => ip.installer_partners?.company_name).filter(Boolean) || [];
      const allTeamNames = [...salesTeamNames, ...installerPartnerNames];

      if (allTeamNames.length > 0) {
        setTeamName(allTeamNames.join(', '));
      } else {
        setTeamName('No team assigned');
      }

      // For backwards compatibility, use the first sales team ID for team_ids
      const firstSalesTeamId = salesTeamData?.[0]?.team_id;
      const teamIds = firstSalesTeamId ? [firstSalesTeamId] : [];

      setNewUser(prev => ({
        ...prev,
        team_ids: teamIds,
        manager_id: user.id,
      }));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load user data.",
        variant: "destructive",
      });
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format as (###) ###-####
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Format phone number as user types
    if (name === 'phone_number') {
      processedValue = formatPhoneNumber(value);
      // Clear error when user starts typing
      if (phoneError) {
        setPhoneError(null);
      }
    }

    // Clear errors when user starts typing
    if (name === 'first_name' && firstNameError) {
      setFirstNameError(null);
    }
    if (name === 'last_name' && lastNameError) {
      setLastNameError(null);
    }
    if (name === 'email' && emailError) {
      setEmailError(null);
    }

    setNewUser(prev => ({ ...prev, [name]: processedValue }));

    if (name === 'email') {
      setAddUserError(null);
      if (value) {
        setIsEmailChecking(true);
        supabase.from('users').select('id').eq('email', value).maybeSingle().then(({ data, error }) => {
          setIsEmailChecking(false);
          if (data && data.id) {
            setAddUserError('A user with this email already exists.');
          } else if (error) {
            setAddUserError('Error checking email uniqueness.');
          }
        });
      }
    }
  };

  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Validate required fields when user leaves the field
    if (name === 'first_name') {
      if (!value.trim()) {
        setFirstNameError('First name is required');
      } else {
        setFirstNameError(null);
      }
    }

    if (name === 'last_name') {
      if (!value.trim()) {
        setLastNameError('Last name is required');
      } else {
        setLastNameError(null);
      }
    }

    if (name === 'email') {
      if (!value.trim()) {
        setEmailError('Email is required');
      } else if (!/\S+@\S+\.\S+/.test(value)) {
        setEmailError('Please enter a valid email');
      } else {
        setEmailError(null);
      }
    }

    // Validate phone number when user leaves the field
    if (name === 'phone_number') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length === 0) {
        setPhoneError('Phone number is required');
      } else if (digitsOnly.length < 10) {
        setPhoneError(`Phone number needs ${10 - digitsOnly.length} more digits`);
      } else if (digitsOnly.length > 10) {
        setPhoneError('Phone number has too many digits');
      } else {
        setPhoneError(null);
      }
    }
  };

  const isPhoneValid = () => /^\d{10}$/.test(newUser.phone_number.replace(/\D/g, ''));

  const handleCreateUser = async () => {
    if (!user) {
      toast({ title: "Error", description: "User not authenticated.", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);

      // Collect all validation errors
      const validationErrors: string[] = [];
      if (!newUser.first_name) validationErrors.push('First name is required');
      if (!newUser.last_name) validationErrors.push('Last name is required');
      if (!newUser.email) validationErrors.push('Email is required');
      if (!newUser.phone_number) validationErrors.push('Phone number is required');
      if (newUser.phone_number && !isPhoneValid()) validationErrors.push('Phone number must be exactly 10 digits');
      if (firstNameError) validationErrors.push(firstNameError);
      if (lastNameError) validationErrors.push(lastNameError);
      if (emailError) validationErrors.push(emailError);
      if (phoneError) validationErrors.push(phoneError);

      if (validationErrors.length > 0) {
        toast({
          title: "Please complete required fields",
          description: [...new Set(validationErrors)].join(" • "),
          variant: "destructive",
        });
        return;
      }

      const { data: existing } = await supabase.from('users').select('id').eq('email', newUser.email).maybeSingle();
      if (existing) {
        setAddUserError('A user with this email already exists.');
        return;
      }

      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({ title: "Error", description: "You must be logged in.", variant: "destructive" });
        return;
      }

      const createData = {
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        email: newUser.email,
        role_id: newUser.role_id,
        phone_number: newUser.phone_number,
        team_ids: newUser.team_ids,
        manager_id: newUser.manager_id,
      };

      const functionsBaseUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '/functions/v1';
      const response = await fetch(`${functionsBaseUrl}/create-admin-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify(createData)
      });

      const data = await response.json();
      if (!response.ok) {
        toast({ title: "Error", description: data.error || 'Failed to create user.', variant: "destructive" });
        return;
      }

      setIsSuccess(true);
      toast({ ...userCreatedToast(data), duration: 5000 });

      setNewUser({
        first_name: "",
        last_name: "",
        email: "",
        role_id: newUser.role_id,
        phone_number: "",
        team_ids: newUser.team_ids,
        manager_id: newUser.manager_id,
      });

      // Close the slide panel after a short delay to show the success message
      setTimeout(() => {
        setIsSuccess(false);
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error: any) {
      toast({ title: "Error", description: error.message || 'Failed to create user.', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 mb-6">
        <h2 className="text-lg font-semibold mb-2">
          {isTeamBasedUser ? "Team Member Information" : "User Information"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isTeamBasedUser
            ? "Add a new member to your team. They will inherit your team settings automatically."
            : "Create a new user account with the specified details."
          }
        </p>
      </div>
      {isSuccess && (
        <div className="text-success font-semibold mb-4">
          User added successfully!
        </div>
      )}

      {/* Scrollable Form Content */}
      <div className="flex-1 overflow-y-auto pr-2">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">
              Contact Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input id="first_name" name="first_name" value={newUser.first_name} onChange={handleInputChange} onBlur={handleBlur} />
                {firstNameError && <p className="text-destructive text-sm mt-1">{firstNameError}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input id="last_name" name="last_name" value={newUser.last_name} onChange={handleInputChange} onBlur={handleBlur} />
                {lastNameError && <p className="text-destructive text-sm mt-1">{lastNameError}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" name="email" type="email" value={newUser.email} onChange={handleInputChange} onBlur={handleBlur} />
              {emailError && <p className="text-destructive text-sm mt-1">{emailError}</p>}
              {addUserError && <p className="text-destructive text-sm mt-1">{addUserError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone *</Label>
              <Input 
                id="phone_number" 
                name="phone_number" 
                type="tel" 
                pattern="[0-9]{10}" 
                placeholder="(123) 456-7890" 
                value={newUser.phone_number} 
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {phoneError && <p className="text-destructive text-sm mt-1">{phoneError}</p>}
            </div>
            {!isTeamBasedUser && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <p className="text-foreground">{roleName || 'Loading...'}</p>
                </div>
                <div className="space-y-2">
                  <Label>Team</Label>
                  <p className="text-foreground">{teamName || 'Loading...'}</p>
                </div>
                <div className="space-y-2">
                  <Label>Manager</Label>
                  <p className="text-foreground">{managerName || 'Loading...'}</p>
                </div>
              </div>
            )}
            {isTeamBasedUser && (
              <div className="space-y-6">
                {/* Role Selection */}
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={newUser.role_id}
                    onValueChange={(value) => {
                      setNewUser(prev => ({ ...prev, role_id: value }));
                      const selectedRole = TEAM_AVAILABLE_ROLES.find(r => r.id === value);
                      setRoleName(selectedRole?.name || '');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {TEAM_AVAILABLE_ROLES.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Permissions & Access */}
                <div className="bg-muted/50 p-4 rounded-lg border">
                  <h4 className="text-sm font-medium mb-3">Permissions & Access</h4>

                  {/* Inherited Settings */}
                  <div className="pt-3 border-t border-border">
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div>
                        <strong>Team:</strong> {teamName || 'Your team'} (inherited)
                      </div>
                      <div>
                        <strong>Manager:</strong> {managerName || 'You'} (inherited)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        </Card>
      </div>

      {/* Fixed Footer with Submit Button */}
      <div className="flex-shrink-0 pt-4 border-t bg-background">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  onClick={handleCreateUser}
                  disabled={loading || isEmailChecking || !!phoneError || !newUser.first_name || !newUser.last_name || !newUser.email || !newUser.phone_number || !isPhoneValid()}
                  className="w-full"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {loading ? "Adding..." : "Add User"}
                </Button>
              </div>
            </TooltipTrigger>
            {(loading || isEmailChecking || !!phoneError || !newUser.first_name || !newUser.last_name || !newUser.email || !newUser.phone_number || !isPhoneValid()) && (
              <TooltipContent side="top" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold">Please complete the following:</p>
                  <ul className="text-xs space-y-0.5">
                    {!newUser.first_name && <li>• First name is required</li>}
                    {!newUser.last_name && <li>• Last name is required</li>}
                    {!newUser.email && <li>• Email address is required</li>}
                    {!newUser.phone_number && <li>• Phone number is required</li>}
                    {newUser.phone_number && !isPhoneValid() && <li>• Phone number must be 10 digits</li>}
                    {phoneError && <li>• {phoneError}</li>}
                    {isEmailChecking && <li>• Checking email availability...</li>}
                  </ul>
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}